// ===============================
// FILE: MyAccountOrder.jsx
// ===============================

import React, {
  useEffect,
  useState,
} from "react";

import api from "../../utils/api";

import io from "socket.io-client";

import {
  GoogleMap,
  Marker,
  Polyline,
  useLoadScript,
} from "@react-google-maps/api";

import "./MyAccountOrder.css";

const socket = io(
  "https://api.freshcart4u.com",
  {
    transports: ["websocket"],
  }
);

const mapContainerStyle = {
  width: "100%",
  height: "250px",
  borderRadius: "15px",
};

const MyAccountOrder = () => {

  const [orders, setOrders] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [cancelLoading, setCancelLoading] =
    useState("");

  const { isLoaded } =
    useLoadScript({
      googleMapsApiKey:
        "YOUR_GOOGLE_MAPS_API_KEY",
    });

  // ================= FETCH ORDERS =================

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {

    try {

      const res = await api.get(
        "/api/orders/my"
      );

      setOrders(res.data || []);

    } catch (err) {

      console.log(err);

    } finally {

      setLoading(false);

    }
  };

  // ================= SOCKET EVENTS =================

  useEffect(() => {

    if (!orders.length) return;

    orders.forEach((order) => {

      socket.emit(
        "joinOrderRoom",
        order._id
      );

    });

    // ETA UPDATE

    socket.on(
      "etaUpdate",
      (data) => {

        setOrders((prev) =>
          prev.map((order) =>
            order._id === data.orderId
              ? {
                  ...order,
                  etaText:
                    data.etaText,
                  distance:
                    data.distance,
                }
              : order
          )
        );
      }
    );

    // STATUS UPDATE

    socket.on(
      "orderStatusUpdated",
      (data) => {

        setOrders((prev) =>
          prev.map((order) =>
            order._id ===
            data.orderId
              ? {
                  ...order,
                  orderStatus:
                    data.status,
                }
              : order
          )
        );
      }
    );

    // LIVE LOCATION UPDATE

    socket.on(
      "deliveryBoyLiveLocation",
      (data) => {

        setOrders((prev) =>
          prev.map((order) =>
            order._id ===
            data.orderId
              ? {
                  ...order,
                  deliveryBoyLocation:
                    {
                      lat: data.lat,
                      lng: data.lng,
                    },
                }
              : order
          )
        );
      }
    );

    return () => {

      socket.off("etaUpdate");

      socket.off(
        "orderStatusUpdated"
      );

      socket.off(
        "deliveryBoyLiveLocation"
      );

    };

  }, [orders]);

  // ================= PAYMENT =================

  const getPaymentLabel = (
    order
  ) => {

    const method =
      order.paymentMethod?.toUpperCase();

    if (
      order.isPaid ||
      method === "PHONEPE" ||
      method === "RAZORPAY" ||
      method === "UPI"
    ) {
      return "💳 Paid Online";
    }

    return "💵 Cash on Delivery";
  };

  const getPaymentClass = (
    order
  ) => {

    const method =
      order.paymentMethod?.toUpperCase();

    if (
      order.isPaid ||
      method === "PHONEPE" ||
      method === "RAZORPAY" ||
      method === "UPI"
    ) {
      return "payment-badge online";
    }

    return "payment-badge cod";
  };

  // ================= CANCEL =================

  const canCancelOrder = (
    status
  ) => {

    const blockedStatuses = [
      "DELIVERED",
      "CANCELLED",
      "OUT_FOR_DELIVERY",
    ];

    return !blockedStatuses.includes(
      status
    );
  };

  const handleCancelOrder =
    async (orderId) => {

      const confirmCancel =
        window.confirm(
          "Are you sure you want to cancel this order?"
        );

      if (!confirmCancel) return;

      try {

        setCancelLoading(orderId);

        const res = await api.put(
          `/api/orders/cancel/${orderId}`
        );

        alert(
          res.data?.message ||
            "Order cancelled"
        );

        setOrders((prev) =>
          prev.map((order) =>
            order._id === orderId
              ? {
                  ...order,
                  orderStatus:
                    "CANCELLED",
                }
              : order
          )
        );

      } catch (err) {

        alert(
          err.response?.data
            ?.message ||
            "Failed to cancel"
        );

      } finally {

        setCancelLoading("");

      }
    };

  // ================= ACTIONS =================

  const handleTrackOrder = (
    orderId
  ) => {

    window.location.href =
      `/order-tracking/${orderId}`;

  };

  const handleReorder = () => {

    window.location.href =
      "/Shop";

  };

  // ================= DOWNLOAD INVOICE =================

const downloadInvoice = async (orderId) => {

  try {

    window.location.href =
      `https://api.freshcart4u.com/api/orders/invoice/pdf/${orderId}`;

  } catch (err) {

    console.log(err);

    alert("Invoice download failed");

  }

};


  
  // ================= TIMELINE =================

  const renderTimeline = (
    status
  ) => {

    if (status === "CANCELLED") {

      return (
        <div className="cancelled-box">
          ❌ Order Cancelled
        </div>
      );
    }

    const steps = [
      "PLACED",
      "PACKING",
      "OUT_FOR_DELIVERY",
      "DELIVERED",
    ];

    const currentIndex =
      steps.indexOf(status);

    return (
      <div className="order-timeline">

        {steps.map(
          (step, index) => {

            const active =
              index <= currentIndex;

            return (
              <div
                key={step}
                className="timeline-step"
              >

                <div
                  className={`timeline-dot ${
                    active
                      ? "active"
                      : ""
                  }`}
                />

                <span
                  className={`timeline-label ${
                    active
                      ? "active"
                      : ""
                  }`}
                >
                  {step.replaceAll(
                    "_",
                    " "
                  )}
                </span>

              </div>
            );
          }
        )}

      </div>
    );
  };

  // ================= LOADING =================

  if (loading) {

    return (
      <div className="order-page">

        <h2>
          Loading Orders...
        </h2>

      </div>
    );
  }

  // ================= EMPTY =================

  if (!orders.length) {

    return (
      <div className="order-empty">

        <h2>
          No Orders Yet 🛒
        </h2>

        <p>
          You haven't placed any
          orders yet.
        </p>

        <a
          href="/"
          className="btn-primary"
        >
          Start Shopping
        </a>

      </div>
    );
  }

  // ================= UI =================

  return (
    <div className="order-page">

      <h1 className="order-title">
        My Orders
      </h1>

      {orders.map((order) => {

        const customerLocation = {
          lat:
            order.shippingAddress
              ?.location?.lat || 0,

          lng:
            order.shippingAddress
              ?.location?.lng || 0,
        };

        const riderLocation =
          order.deliveryBoyLocation;

        return (

          <div
            key={order._id}
            className="order-card"
          >

            {/* HEADER */}

            <div className="order-header">

              <div>

                <p className="order-id">
                  Order #
                  {order._id?.slice(
                    -6
                  )}
                </p>

                <span
                  className={`status ${(
                    order.orderStatus ||
                    "PLACED"
                  ).toLowerCase()}`}
                >
                  {order.orderStatus ||
                    "PLACED"}
                </span>

              </div>

              <div className="order-price">
                ₹
                {order.totalAmount ||
                  order.totalPrice ||
                  0}
              </div>

            </div>

            {/* PAYMENT */}

            <div
              className={getPaymentClass(
                order
              )}
            >
              {getPaymentLabel(order)}
            </div>

            {/* ETA */}

            {order.etaText &&
              order.orderStatus !==
                "DELIVERED" &&
              order.orderStatus !==
                "CANCELLED" && (

                <div className="order-eta">

                  🚴 Delivery Partner is{" "}
                  <strong>
                    {order.distance}
                  </strong>{" "}
                  away

                  <br />

                  ⏳ Expected delivery in{" "}
                  <strong>
                    {order.etaText}
                  </strong>

                </div>
              )}

            {/* MAP */}

            {isLoaded &&
              riderLocation &&
              customerLocation && (

                <div className="map-box">

                  <GoogleMap
                    mapContainerStyle={
                      mapContainerStyle
                    }
                    zoom={14}
                    center={riderLocation}
                  >

                    <Marker
                      position={
                        riderLocation
                      }
                    />

                    <Marker
                      position={
                        customerLocation
                      }
                    />

                    <Polyline
                      path={[
                        riderLocation,
                        customerLocation,
                      ]}
                      options={{
                        strokeColor:
                          "#00b894",
                        strokeOpacity:1,
                        strokeWeight:5,
                      }}
                    />

                  </GoogleMap>

                </div>
              )}

            {/* TIMELINE */}

            {renderTimeline(
              order.orderStatus ||
                "PLACED"
            )}

            {/* TRACKING DETAILS */}

            {order.orderStatus !==
              "CANCELLED" && (

              <div className="tracking-details-card">

                <h3 className="tracking-title">
                  🚚 Live Tracking Details
                </h3>

                <div className="tracking-row">

                  <span>
                    Order Status :
                  </span>

                  <strong>
                    {order.orderStatus ||
                      "PLACED"}
                  </strong>

                </div>

                {order.etaText && (

                  <div className="tracking-row">

                    <span>
                      Delivery ETA :
                    </span>

                    <strong>
                      {order.etaText}
                    </strong>

                  </div>
                )}

                {order.distance && (

                  <div className="tracking-row">

                    <span>
                      Distance :
                    </span>

                    <strong>
                      {order.distance}
                    </strong>

                  </div>
                )}

                {/* DELIVERY BOY */}

                

{order.delivery?.deliveryBoy && (
                  <div className="delivery-boy-card">

                    <div className="delivery-boy-top">


<img
  src={
    order.delivery?.deliveryBoy?.image ||
    "https://cdn-icons-png.flaticon.com/512/194/194938.png"
  }
  alt="Delivery Boy"
  className="delivery-boy-img"
/>

                      <div>

                      <h4>
  {order.delivery?.deliveryBoy?.name || "Delivery Partner"}
</h4>

                        
<p className="delivery-boy-phone">
  📞 {order.delivery?.deliveryBoy?.phone || "Not Available"}
</p>

                      </div>

                    </div>

                    <div className="delivery-actions">

                     

<a
  href={`tel:${order.delivery?.deliveryBoy?.phone}`}
  className="delivery-call-btn"
>
  📞 Call Now
</a>

<a
  href={`https://wa.me/91${order.delivery?.deliveryBoy?.phone}`}
  target="_blank"
  rel="noreferrer"
  className="delivery-whatsapp-btn"
>
  💬 WhatsApp
</a>
                 

                    </div>

                  </div>
                )}

              </div>
            )}

            {/* ITEMS */}

            <div className="order-items">

              {order.items?.map(
                (item, idx) => (

                  <div
                    key={idx}
                    className="order-item"
                  >

                    <img
                      src={
                        item.image ||
                        "/placeholder.png"
                      }
                      alt={item.name}
                    />

                    <div>

                      <p className="item-name">
                        {item.name}
                      </p>

                      <p className="item-qty">
                        {
                          item.quantity
                        } × ₹
                        {item.price}
                      </p>

                    </div>

                  </div>
                )
              )}

            </div>

            {/* ADDRESS */}

            <div className="order-address">

              <strong>
                Delivery Address
              </strong>

              <p>

                {order.shippingAddress
                  ?.addressLine1 || ""}

                {order.shippingAddress
                  ?.city &&
                  `, ${order.shippingAddress.city}`}

                {order.shippingAddress
                  ?.pincode &&
                  ` - ${order.shippingAddress.pincode}`}

              </p>

            </div>

            {/* FOOTER */}

            <div className="order-footer">

              <div className="order-actions">

                <button
                  type="button"
                  className="btn-outline"
                  onClick={() =>
                    downloadInvoice(
                      order._id
                    )
                  }
                >
                  Download Invoice
                </button>

                {order.orderStatus !==
                  "CANCELLED" && (

                  <button
                    className="btn-track"
                    onClick={() =>
                      handleTrackOrder(
                        order._id
                      )
                    }
                  >
                    Track Order
                  </button>
                )}

                {canCancelOrder(
                  order.orderStatus
                ) && (

                  <button
                    className="btn-cancel"
                    disabled={
                      cancelLoading ===
                      order._id
                    }
                    onClick={() =>
                      handleCancelOrder(
                        order._id
                      )
                    }
                  >
                    {cancelLoading ===
                    order._id
                      ? "Cancelling..."
                      : "Cancel Order"}
                  </button>
                )}

                <button
                  className="btn-primary"
                  onClick={
                    handleReorder
                  }
                >
                  Re-Order
                </button>

              </div>

            </div>

          </div>
        );
      })}
    </div>
  );
};

export default MyAccountOrder;